// frontend/src/components/pacientes/HistorialCambiosEstado.jsx
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import pacienteService from '../../services/pacienteService';

const HistorialCambiosEstado = ({ pacienteId }) => {
  const [cambios, setCambios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCambio, setSelectedCambio] = useState(null);

  useEffect(() => {
    const fetchCambios = async () => {
      if (!pacienteId) return;
      
      try {
        setLoading(true);
        const response = await pacienteService.getCambiosEstadoPaciente(pacienteId);
        setCambios(response.data);
      } catch (error) {
        console.error('Error al cargar historial de cambios de estado:', error);
        setError('No se pudo cargar el historial de cambios de estado');
      } finally {
        setLoading(false);
      }
    };

    fetchCambios();
  }, [pacienteId]);

  const getColorByEstado = (estado) => {
    switch (estado) {
      case 'Activo':
        return 'success';
      case 'Inactivo':
        return 'error';
      case 'De alta':
        return 'info';
      case 'Pausa':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleOpenDetail = (cambio) => {
    setSelectedCambio(cambio);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (cambios.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="textSecondary">
          No hay cambios de estado registrados para este paciente.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Estado Anterior</TableCell>
              <TableCell>Nuevo Estado</TableCell>
              <TableCell>Motivo</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Detalles</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cambios.map((cambio) => (
              <TableRow key={cambio.id}>
                <TableCell>
                  {format(new Date(cambio.fecha_cambio), 'dd/MM/yyyy HH:mm', { locale: es })}
                </TableCell>
                <TableCell>
                  {cambio.estado_anterior ? (
                    <Chip 
                      label={cambio.estado_anterior}
                      size="small"
                      color={getColorByEstado(cambio.estado_anterior)}
                    />
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      Estado inicial
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={cambio.estado_nuevo}
                    size="small"
                    color={getColorByEstado(cambio.estado_nuevo)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap style={{ maxWidth: 200 }}>
                    {cambio.motivo}
                  </Typography>
                </TableCell>
                <TableCell>
                  {cambio.usuario?.nombre} {cambio.usuario?.apellidos}
                </TableCell>
                <TableCell>
                  <Tooltip title="Ver detalles">
                    <IconButton size="small" onClick={() => handleOpenDetail(cambio)}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de detalles */}
      <Dialog open={detailOpen} onClose={handleCloseDetail}>
        <DialogTitle>Detalles del Cambio de Estado</DialogTitle>
        <DialogContent dividers>
          {selectedCambio && (
            <Box>
              <Typography variant="subtitle2">Fecha y hora:</Typography>
              <Typography variant="body2" paragraph>
                {format(new Date(selectedCambio.fecha_cambio), 'dd/MM/yyyy HH:mm:ss', { locale: es })}
              </Typography>
              
              <Typography variant="subtitle2">Cambio de estado:</Typography>
              <Box display="flex" alignItems="center" mb={2}>
                {selectedCambio.estado_anterior ? (
                  <Chip 
                    label={selectedCambio.estado_anterior}
                    size="small"
                    color={getColorByEstado(selectedCambio.estado_anterior)}
                    style={{ marginRight: 8 }}
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
                    Estado inicial
                  </Typography>
                )}
                <Typography variant="body2" style={{ margin: '0 8px' }}>→</Typography>
                <Chip 
                  label={selectedCambio.estado_nuevo}
                  size="small"
                  color={getColorByEstado(selectedCambio.estado_nuevo)}
                />
              </Box>
              
              <Typography variant="subtitle2">Motivo del cambio:</Typography>
              <Typography variant="body2" paragraph>
                {selectedCambio.motivo}
              </Typography>
              
              {selectedCambio.observaciones && (
                <>
                  <Typography variant="subtitle2">Observaciones adicionales:</Typography>
                  <Typography variant="body2" paragraph>
                    {selectedCambio.observaciones}
                  </Typography>
                </>
              )}
              
              <Typography variant="subtitle2">Realizado por:</Typography>
              <Typography variant="body2">
                {selectedCambio.usuario?.nombre} {selectedCambio.usuario?.apellidos}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HistorialCambiosEstado;